import config from 'src/shared/config';
import axios from 'src/shared/utils/axios';
import { toLocalDateTimeStr } from 'src/shared/utils/localDateTime';
import DummyApi from '../dummyApi';
import { ApprovalDetailDto, ApprovalDto, ApproverDto } from './approvalApi';
const FXM_API_ENABLED = config.servers.fxm.enabled;
const FXM_API_URL = config.servers.fxm.url;
export enum ScreenExceptTarget {
  SWM = 'SWM', // 스크린 워터마크
  BLKSC = 'BLKSC', // 캡처 방지
}
export enum ScreenStatus {
  CARRAYOUT = 'CARRAYOUT', // 회수
  EXCEPT = 'EXCEPT', // 화면 보안 예외 적용
  EXPIRED = 'EXPIRED', // 만료 (시스템에 의해)
  NOT_APPLIED = 'NOT_APPLIED', // 정책 서버 미적용 상태
}
export interface ProcessDto {
  code: string; // 프로세스 고유 코드(공통 서버의 프로그램 목록)
  value: string; //프로세스 이름(공통 서버의 프로그램 이름)
}
export interface ScreenApprovalDto extends ApprovalDto {
  startDate: number; // 요청 시작 시간
  endDate: number; // 요청 종료 시간
  exceptTargetList: string[]; // 적용 범위: SWM(스크린 워터마크), BLKSC(캡처 방지) 중 1개 이상 필수
  processTargetAll: string; // 적용 대상 프로세스 전체(Y), 일부 (N)
  processes: ProcessDto[]; //적용 대상 프로세스 목록. processTargetAll가 Y 일땐 [], N 일 경우 리스트 세팅
  approvalNo: string;
  exceptScreenStatus: string;
}
export interface ScreenApprovalDetailDto extends ApprovalDetailDto {
  startDate: number;
  endDate: number;
  exceptTargetList: string[]; // 적용 범위: SWM(스크린 워터마크), BLKSC(캡처 방지) 중 1개 이상 필수
  processTargetAll: string; // 적용 대상 프로세스 전체(Y), 일부 (N)
  processes: ProcessDto[]; //적용 대상 프로세스 목록. processTargetAll가 Y 일땐 [], N 일 경우 리스트 세팅
  exceptScreenStatus: string;
}
export interface ScreenApprovalDetailEntity extends ScreenApprovalDetailDto {
  startDateStr: string;
  endDateStr: string;
  applyDateStr: string;
  updatedTsStr: string;
}
export const toScreenApprovalDetailDtoToEntity = (
  dto: ScreenApprovalDetailDto
): ScreenApprovalDetailEntity => {
  return {
    ...dto,
    startDateStr: dto.startDate > 0 ? toLocalDateTimeStr(dto.startDate) : '',
    endDateStr: dto.endDate > 0 ? toLocalDateTimeStr(dto.endDate) : '',
    applyDateStr: dto.applyDate > 0 ? toLocalDateTimeStr(dto.applyDate) : '',
    updatedTsStr: dto.updatedTs > 0 ? toLocalDateTimeStr(dto.updatedTs) : '',
    progressApprovers: dto.progressApprovers === null ? [] : dto.progressApprovers,
    completeApprovers: dto.completeApprovers === null ? [] : dto.completeApprovers,
  };
};
export type ScreenApprovalReqDto = Omit<ScreenApprovalDto, 'approvalNo' | 'exceptScreenStatus'>;
export interface ScreenApprovalServerReqDto extends ApprovalDto {
  startDate: number; // 요청 시작 시간
  endDate: number; // 요청 종료 시간
  exceptTargetList: string[]; // 적용 범위: SWM(스크린 워터마크), BLKSC(캡처 방지) 중 1개 이상 필수
  processTargetAll: string; // 적용 대상 프로세스 전체(Y), 일부 (N)
  processes: ProcessDto[]; //적용 대상 프로세스 목록. processTargetAll가 Y 일땐 [], N 일 경우 리스트 세팅
}
export const convertScreenApprovalReqDtoToServerReq = (
  reqDto: ScreenApprovalReqDto
): ScreenApprovalServerReqDto => {
  return {
    code: reqDto.code,
    approvalStep: reqDto.approvalStep,
    currentStep: reqDto.currentStep,
    approvalType: reqDto.approvalType,
    status: reqDto.status,
    approvalMethod: reqDto.approvalMethod,
    remark: reqDto.remark,
    approvers: reqDto.approvers,
    startDate: reqDto.startDate,
    endDate: reqDto.endDate,
    exceptTargetList: reqDto.exceptTargetList,
    processTargetAll: reqDto.processTargetAll,
    processes: reqDto.processes,
  };
};
interface ScreenApprovalApi {
  find(code: string): Promise<ScreenApprovalDetailEntity>;
  apply(reqDto: ScreenApprovalReqDto): Promise<ScreenApprovalDto>;
  cancel(approvalNo: string): Promise<boolean>;
  carryout(approvalNo: string): Promise<boolean>;
}
class ScreenApprovalServerApi implements ScreenApprovalApi {
  async find(code: string): Promise<ScreenApprovalDetailEntity> {
    const resp = await axios.get<ScreenApprovalDetailDto>(`${FXM_API_URL}/api/screen/find/${code}`);
    return toScreenApprovalDetailDtoToEntity(resp.data);
  }
  async apply(reqDto: ScreenApprovalReqDto): Promise<ScreenApprovalDetailDto> {
    const reqServerDto: ScreenApprovalServerReqDto = convertScreenApprovalReqDtoToServerReq(reqDto);
    const resp = await axios.post<ScreenApprovalDetailDto>(
      `${FXM_API_URL}/api/screen/apply`,
      reqServerDto
    );
    return resp.data;
  }
  async cancel(approvalNo: string): Promise<boolean> {
    const resp = await axios.get<ScreenApprovalDetailDto>(
      `${FXM_API_URL}/api/screen/cancel/${approvalNo}`
    );
    if ([400, 500].includes(resp.status)) {
      return false;
    }
    return true;
  }
  async carryout(approvalNo: string): Promise<boolean> {
    const resp = await axios.get<ScreenApprovalDetailDto>(
      `${FXM_API_URL}/api/screen/carryout/${approvalNo}`
    );
    if ([400, 500].includes(resp.status)) {
      return false;
    }
    return true;
  }
}
const screenApprovalApi = new ScreenApprovalServerApi();
export default screenApprovalApi;
